<ng-container *ngIf="objectIdentifier && contractDto">

  <!-- TM-1000001554: Render the contract header INLINE in the page template (not via
       <ng-template #headerTemplate> + getPageHeaderConfig + shared <mvs-page-header>).
       In shell-consultant the <mvs-page-header> lives at the top of the main layout,
       OUTSIDE the object-navigation overlay. When the page was opened in an overlay
       the header rendered in the wrong slot, so the overlay showed its inner tabs
       above the header instead of below it. Inline rendering guarantees correct
       order: header first, then the contract component (which carries the tabs). -->
  <div class="flex justify-content-between w-full align-items-center surface-0 px-4 py-3 contract-sticky-header">

    <!-- Left: Contract type + Customer link + Partner info -->
    <div class="flex align-items-center">

      <div class="flex gap-3 align-items-center">
        <i *ngIf="contractDto.contractTypeDto && contractDto.contractTypeDto.image"
           class="fa-2x {{contractDto.contractTypeDto.image}}"></i>

        <div class="mr-5">
          <h5 class="mb-0">{{ contractDto.contractTypeDto?.name }}</h5>
          <small *ngIf="contractDto.joinCurrentMainCustomer">
            <a [routerLink]="'/cr/customers/' + contractDto.joinCurrentMainCustomer.id"
               class="text-primary font-semibold no-underline hover:underline">
              {{ contractDto.joinCurrentMainCustomer?.personDto?.firstName }} {{ contractDto.joinCurrentMainCustomer?.personDto?.lastName }}
            </a>
          </small>
        </div>
      </div>

      <p-divider layout="vertical" styleClass="border-1 border-300"
                 *ngIf="contractDto.partnerDto"></p-divider>

      <!-- Left "…": all successor contracts (Zukunftsverträge), from the preloaded succession data -->
      @if (succession.hasSuccessor) {
        <p-button
          class="mr-4"
          icon="fa-solid fa-ellipsis"
          [rounded]="true"
          [text]="true"
          pTooltip="Alle Zukunftsverträge anzeigen"
          tooltipPosition="bottom"
          ariaLabel="Alle Zukunftsverträge anzeigen"
          (click)="openSuccessorHistory($event, successorContractOP)"
        />
      }

      <div class="flex align-items-center">

        <!--
          Successor contract (Zukunftsvertrag), mirrored to the predecessor on the right: the newest one
          (the backend sorts newest first). Shown whenever a successor exists - the partner parts are
          guarded one by one. Empty while the succession loads (no placeholder, signed S-9).
          Keyboard: the partner name and the VSN chip are the two links (role="link", Enter); the logo
          and the icon tile are decorative (aria-hidden, no click of their own) - the name next to them
          carries the partner link.
        -->
        @if (succession.successorContract; as successor) {
          <div class="flex gap-3 align-items-center surface-50 px-3 py-1 border-round">
            <!-- Successor Partner Logo: Image URL (decorative) -->
            <img class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg cm-partner-logo"
                 *ngIf="successor.partnerDto?.image && isImageUrl(successor.partnerDto.image)"
                 [src]="successor.partnerDto.image"
                 alt="" aria-hidden="true"/>
            <!-- Successor Partner Logo: Font Awesome Icon (decorative) -->
            <div class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg flex align-items-center
                        justify-content-center surface-100"
                 aria-hidden="true"
                 *ngIf="successor.partnerDto?.image && !isImageUrl(successor.partnerDto.image)">
              <i class="text-2xl text-600 {{successor.partnerDto.image}}"></i>
            </div>
            <div class="flex flex-column gap-1">
              <span class="font-medium cursor" *ngIf="successor.partnerDto"
                    role="link" tabindex="0"
                    (click)="handleNavigateToPartner(successor.partnerDto?.id)"
                    (keydown.enter)="handleNavigateToPartner(successor.partnerDto?.id)"
                    pTooltip="Vertragspartner" tooltipPosition="bottom">
                {{ successor.partnerDto.name }}
              </span>
              <div class="flex align-items-center gap-2">
                <!-- the successor's VSN opens that contract in a new tab (click + Enter), like the predecessor's -->
                <span *ngIf="successor.contractIdentifier"
                      class="surface-100 border-round-lg p-2 w-max cursor-pointer"
                      pTooltip="Zukunftsvertrag öffnen" tooltipPosition="bottom" role="link" tabindex="0"
                      (click)="handleNavigateToSuccessorContract(successor.id)"
                      (keydown.enter)="handleNavigateToSuccessorContract(successor.id)"
                >{{ successor.contractIdentifier }}</span>
                <!-- a never-active successor carries the „Nie aktiv“ tag INSTEAD of its status tag (WR2, S-16) -->
                @if (neverActive.marker(successor); as marker) {
                  <p-tag class="cm-never-active-tag bg-orange-200 text-orange-800" role="img"
                         icon="fa-solid fa-calendar-xmark"
                         [value]="marker.label"
                         [attr.aria-label]="marker.description" [pTooltip]="marker.description"
                         tooltipPosition="bottom"/>
                } @else {
                  <p-tag *ngIf="successor.baseStatusEnumName"
                         icon="{{successor.baseStatusEnumImage}}"
                         value="{{ successor.baseStatusEnumName }}"
                         styleClass="bg-{{ successor.baseStatusEnumColorBackground }}
                                     text-{{ successor.baseStatusEnumColor }}"/>
                }
              </div>
            </div>
          </div>

          <!--
            connector: the arrow points from the older (this contract) to the newer contract (the successor),
            in time direction. Dashed with its own words as soon as one end never became active (WR6, S-17).
            The dashed divider needs border-dashed: border-1 alone forces a SOLID host border (C-N2).
          -->
          @switch (successorConnectorKind) {
            @case ('normal') {
              <!-- both ends became active: solid, text of 20.10. (unchanged) -->
              <div class="flex align-items-center relative" role="img"
                   aria-label="Dieser Vertrag wird durch den Zukunftsvertrag abgelöst"
                   pTooltip="Dieser Vertrag wird durch den Zukunftsvertrag abgelöst" tooltipPosition="bottom">
                <p-divider layout="horizontal" styleClass="border-1 border-300 m-0"></p-divider>
                <i class="fa-sharp fa-regular fa-arrow-left text-400 absolute right-0 left-0 w-1rem m-auto surface-0"
                   aria-hidden="true"></i>
              </div>
            }
            @case ('newerNeverActive') {
              <div class="flex align-items-center relative" role="img"
                   aria-label="Der Zukunftsvertrag sollte diesen Vertrag ablösen, wurde aber nie aktiv"
                   pTooltip="Der Zukunftsvertrag sollte diesen Vertrag ablösen, wurde aber nie aktiv"
                   tooltipPosition="bottom">
                <p-divider layout="horizontal" type="dashed"
                           class="border-1 border-300 border-dashed m-0"></p-divider>
                <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                   aria-hidden="true"></i>
              </div>
            }
            @case ('olderNeverActive') {
              <div class="flex align-items-center relative" role="img"
                   aria-label="Dieser Vertrag wurde nie aktiv, der Zukunftsvertrag tritt an seine Stelle"
                   pTooltip="Dieser Vertrag wurde nie aktiv, der Zukunftsvertrag tritt an seine Stelle"
                   tooltipPosition="bottom">
                <p-divider layout="horizontal" type="dashed"
                           class="border-1 border-300 border-dashed m-0"></p-divider>
                <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                   aria-hidden="true"></i>
              </div>
            }
            @case ('bothNeverActive') {
              <div class="flex align-items-center relative" role="img"
                   aria-label="Dieser Vertrag und der Zukunftsvertrag wurden beide nie aktiv"
                   pTooltip="Dieser Vertrag und der Zukunftsvertrag wurden beide nie aktiv"
                   tooltipPosition="bottom">
                <p-divider layout="horizontal" type="dashed"
                           class="border-1 border-300 border-dashed m-0"></p-divider>
                <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                   aria-hidden="true"></i>
              </div>
            }
          }
        }

        <!-- current contract: boxed as soon as a predecessor OR a successor stands next to it -->
        <div class="flex gap-3 align-items-center"
             [ngClass]="(contractDto?.formerContractDto?.partnerDto || succession.hasSuccessor)
                 ? 'surface-50 px-3 py-1 border-round' : ''">
          <!-- Partner Logo: Image URL -->
          <img class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg cursor" style="object-fit:contain"
               *ngIf="contractDto.partnerDto?.image && isImageUrl(contractDto.partnerDto.image)"
               [src]="contractDto.partnerDto.image"
               alt="Contract Partner Image"/>
          <!-- Partner Logo: Font Awesome Icon -->
          <div class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg cursor flex align-items-center justify-content-center surface-100"
               *ngIf="contractDto.partnerDto?.image && !isImageUrl(contractDto.partnerDto.image)">
            <i class="text-2xl text-600 {{contractDto.partnerDto.image}}"></i>
          </div>
          <div class="flex flex-column gap-1">
            <span class="font-medium cursor" *ngIf="contractDto.partnerDto"
                  pTooltip="Contract Partner" tooltipPosition="bottom">
              {{ contractDto.partnerDto.name }}
            </span>
            <!-- the current contract has no status tag; a never-active one gets the „Nie aktiv“ tag right of
                 its VSN chip (WR5, S-16/S-19) - also without neighbours, then without box styling -->
            <div class="flex align-items-center gap-2">
              <span *ngIf="contractDto.contractIdentifier"
                    class="surface-100 border-round-lg p-2 w-max" pTooltip="Contract Identifier"
                    tooltipPosition="bottom">{{ contractDto.contractIdentifier }}</span>
              @if (neverActive.marker(contractDto); as marker) {
                <p-tag class="cm-never-active-tag bg-orange-200 text-orange-800" role="img"
                       icon="fa-solid fa-calendar-xmark"
                       [value]="marker.label"
                       [attr.aria-label]="marker.description" [pTooltip]="marker.description"
                       tooltipPosition="bottom"/>
              }
            </div>
          </div>
        </div>

        @for (entry of formerContractMapping; track entry.contractId) {
          <!--
            connector: the arrow points from the older (the predecessor) to the newer contract (this one), in
            time direction. Dashed with its own words as soon as one end never became active (WR6, S-17).
            The dashed divider needs border-dashed: border-1 alone forces a SOLID host border (C-N2).
          -->
          @if (entry?.formerContractDto?.partnerDto) {
            @switch (formerConnectorKind) {
              @case ('normal') {
                <!-- both ends became active: solid, text of 20.10. (unchanged) -->
                <div class="flex align-items-center relative" role="img"
                     aria-label="Der Vorvertrag wird durch diesen Vertrag abgelöst"
                     pTooltip="Der Vorvertrag wird durch diesen Vertrag abgelöst" tooltipPosition="bottom">
                  <p-divider layout="horizontal" styleClass="border-1 border-300 m-0"></p-divider>
                  <i class="fa-sharp fa-regular fa-arrow-left text-400 absolute right-0 left-0 w-1rem m-auto surface-0"
                     aria-hidden="true"></i>
                </div>
              }
              @case ('newerNeverActive') {
                <div class="flex align-items-center relative" role="img"
                     aria-label="Dieser Vertrag sollte den Vorvertrag ablösen, wurde aber nie aktiv"
                     pTooltip="Dieser Vertrag sollte den Vorvertrag ablösen, wurde aber nie aktiv"
                     tooltipPosition="bottom">
                  <p-divider layout="horizontal" type="dashed"
                             class="border-1 border-300 border-dashed m-0"></p-divider>
                  <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                     aria-hidden="true"></i>
                </div>
              }
              @case ('olderNeverActive') {
                <div class="flex align-items-center relative" role="img"
                     aria-label="Der Vorvertrag wurde nie aktiv, dieser Vertrag tritt an seine Stelle"
                     pTooltip="Der Vorvertrag wurde nie aktiv, dieser Vertrag tritt an seine Stelle"
                     tooltipPosition="bottom">
                  <p-divider layout="horizontal" type="dashed"
                             class="border-1 border-300 border-dashed m-0"></p-divider>
                  <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                     aria-hidden="true"></i>
                </div>
              }
              @case ('bothNeverActive') {
                <div class="flex align-items-center relative" role="img"
                     aria-label="Der Vorvertrag und dieser Vertrag wurden beide nie aktiv"
                     pTooltip="Der Vorvertrag und dieser Vertrag wurden beide nie aktiv"
                     tooltipPosition="bottom">
                  <p-divider layout="horizontal" type="dashed"
                             class="border-1 border-300 border-dashed m-0"></p-divider>
                  <i class="fa-sharp fa-regular fa-arrow-left text-300 absolute right-0 left-0 w-1rem m-auto surface-0"
                     aria-hidden="true"></i>
                </div>
              }
            }
          }

          <div class="flex gap-3 align-items-center surface-50 px-3 py-1 border-round"
               *ngIf="entry?.formerContractDto?.partnerDto">
            <!-- Former Partner Logo: Image URL -->
            <img class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg cursor" style="object-fit:contain"
                 (click)="handleNavigateToPartner(entry?.formerContractDto?.partnerDto?.id)"
                 *ngIf="entry?.formerContractDto?.partnerDto?.image && isImageUrl(entry.formerContractDto.partnerDto.image)"
                 [src]="entry?.formerContractDto?.partnerDto?.image"
                 alt="Contract Partner Image"/>
            <!-- Former Partner Logo: Font Awesome Icon -->
            <div class="w-3rem h-3rem border-1 border-300 p-1 border-round-lg cursor flex align-items-center justify-content-center surface-100"
                 (click)="handleNavigateToPartner(entry?.formerContractDto?.partnerDto?.id)"
                 *ngIf="entry?.formerContractDto?.partnerDto?.image && !isImageUrl(entry.formerContractDto.partnerDto.image)">
              <i class="text-2xl text-600 {{entry.formerContractDto.partnerDto.image}}"></i>
            </div>
            <div class="flex flex-column gap-1">
              <span class="font-medium cursor" *ngIf="entry?.formerContractDto?.partnerDto"
                    (click)="handleNavigateToPartner(entry?.formerContractDto?.partnerDto?.id)"
                    pTooltip="Contract Partner" tooltipPosition="bottom">
                {{ entry?.formerContractDto?.partnerDto?.name }}
              </span>
              <div class="flex align-items-center gap-2">
                <!-- TM-1000002018: the former contract's VSN opens that contract in a new tab (click + Enter) -->
                <span *ngIf="entry?.formerContractDto?.contractIdentifier"
                      class="surface-100 border-round-lg p-2 w-max cursor-pointer" pTooltip="Vorvertrag öffnen"
                      tooltipPosition="bottom" role="link" tabindex="0"
                      (click)="handleNavigateToFormerContract(entry?.formerContractDto?.id)"
                      (keydown.enter)="handleNavigateToFormerContract(entry?.formerContractDto?.id)">{{ entry?.formerContractDto?.contractIdentifier }}</span>
                <!-- a never-active predecessor carries the „Nie aktiv“ tag
                     INSTEAD of its status tag (WR4, S-16) -->
                @if (neverActive.marker(entry?.formerContractDto); as marker) {
                  <p-tag class="cm-never-active-tag bg-orange-200 text-orange-800" role="img"
                         icon="fa-solid fa-calendar-xmark"
                         [value]="marker.label"
                         [attr.aria-label]="marker.description" [pTooltip]="marker.description"
                         tooltipPosition="bottom"/>
                } @else {
                  <p-tag *ngIf="entry?.formerContractDto?.partnerDto"
                         icon="{{entry?.formerContractDto?.baseStatusEnumImage}}"
                         value="{{ entry?.formerContractDto?.baseStatusEnumName }}"
                         styleClass="bg-{{ entry?.formerContractDto?.baseStatusEnumColorBackground }} text-{{ entry?.formerContractDto?.baseStatusEnumColor }}"/>
                }
              </div>
            </div>
          </div>
        }

      </div>

      <!-- Right "…": the predecessor tree from the preloaded succession data; spinner while it loads -->
      @if (showMoreFormerButton) {
        <p-button
          class="ml-4"
          [icon]="succession.isLoading ? 'fa-solid fa-spinner-third fa-spin' : 'fa-solid fa-ellipsis'"
          [rounded]="true"
          [text]="true"
          (click)="openFormerHistory($event, formerContractOP)"
        />
      }

    </div>

    <!-- Right: Contract navigation + Create ticket -->
    <div class="flex align-items-center gap-3">
      <cm-contract-navigation-contract class="surface-100 p-2 border-round-lg"
                                       (onSelect)="handleIdSelect($event)">
      </cm-contract-navigation-contract>

      <div class="flex align-items-center border-1 border-400 border-round" style="height: 2.5rem">
        <div class="px-3 py-1 cursor hover:surface-hover" (click)="createTicket()" pTooltip="Ticket erstellen" tooltipPosition="top">
          <i class="fa-regular fa-ticket text-2xl pt-1 text-primary"></i>
        </div>
      </div>
    </div>

  </div>

  <!-- Show Object -->
  <mvs-cm-contract-consultant
    [objectIdentifier]="objectIdentifier"
    [viewType]="viewType"
    [transientDto]="contractDto"
  ></mvs-cm-contract-consultant>

</ng-container>

<p-popover #formerContractOP appendTo="body" (onHide)="showFormerContract = false">
  <mvs-former-contract-mapping
    *ngIf="objectIdentifier && showFormerContract && contractFormerHistoryNodeDto"
    [contractFormerHistoryNodeDto]="contractFormerHistoryNodeDto"
    [neverActiveMarker]="neverActive.marker"
  ></mvs-former-contract-mapping>
</p-popover>

<!-- Successor tree (Zukunftsverträge), opened by the left "…"; both org charts mark never-active nodes (F7) -->
<p-popover #successorContractOP appendTo="body" (onHide)="showSuccessorContract = false">
  <mvs-former-contract-mapping
    *ngIf="showSuccessorContract && succession.successorHistory"
    [heading]="'Zukunftsverträge'"
    [contractFormerHistoryNodeDto]="succession.successorHistory"
    [neverActiveMarker]="neverActive.marker"
  ></mvs-former-contract-mapping>
</p-popover>


<p-button type="button" (click)="openSidebar()" icon="pi pi-link text-2xl" *ngIf="!displaySidebar" class="layout-config-button"></p-button>
<p-drawer
  #sidebar
  [(visible)]="displaySidebar"
  [position]="'right'"
  [blockScroll]="false"
  [showCloseIcon]="false"
  [baseZIndex]="1000"
  [styleClass]="'layout-rightmenu p-sidebar-sm fs-small p-3 w-30rem'"
>
  <ng-container *ngIf="displaySidebar">
    <external-partner-link
      [customerId]="contractDto?.joinCurrentMainCustomer?.id"
      [contract]="contractDto"
    ></external-partner-link>
    <cw-application-launch
      [customerId]="contractDto?.joinCurrentMainCustomer?.id"
      [contract]="contractDto"
    ></cw-application-launch>
  </ng-container>
</p-drawer>
